import React, { useMemo } from 'react';
import { Chip, Paper, Stack, Typography } from '@mui/material';

import type { RoleAssignmentSummaryDTO } from '@/api/generated';
import HiddenItemCounter from '@/components/HiddenItemCounter/HiddenItemCounter';
import { borders } from '@/theme/constants';
import typography from '@/theme/typography';

interface RoleDisplayProps {
  value?: RoleAssignmentSummaryDTO[];
}

const MAX_VISIBLE_ROLES = 2;

const RoleDisplay: React.FC<RoleDisplayProps> = ({ value }) => {
  const visibleRoles = useMemo(() => value?.slice(0, MAX_VISIBLE_ROLES), [value]);
  const hiddenRoles = useMemo(() => value?.slice(MAX_VISIBLE_ROLES), [value]);
  const hiddenItemCounterValue = hiddenRoles?.length ?? 0;
  if (!value || value.length === 0) {
    return (
      <Typography variant="body2" color="text.secondary">
        –
      </Typography>
    );
  }

  return (
    <Stack direction="row" gap={0.5} flexWrap="nowrap" alignItems="center">
      {visibleRoles?.map(role => (
        <Chip
          key={role.id || role.name}
          label={role.name || '—'}
          size="small"
          title={role.name || role.id}
          sx={{
            height: 24,
            border: borders.default,
            borderColor: 'grey.400',
            bgcolor: 'transparent',
            maxWidth: 100,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap'
          }}
        />
      ))}

      {hiddenItemCounterValue > 0 && (
        <HiddenItemCounter hiddenItemsCount={hiddenItemCounterValue} sx={{ paddingInlineStart: 0 }}>
          <Paper
            sx={{
              position: 'relative',
              minWidth: '87px',
              p: 1,
              '& .MuiTypography-root': { color: 'text.primary', ...typography.body3 }
            }}
          >
            <Stack maxHeight="200px" overflow="auto" p={1}>
              <Stack gap={1}>{hiddenRoles?.map(role => <Typography key={role.id}>{role.name}</Typography>)}</Stack>
            </Stack>
          </Paper>
        </HiddenItemCounter>
      )}
    </Stack>
  );
};

export default RoleDisplay;
